import DOMPurify from 'dompurify';

import { chatActionTypes } from './constants';
import { appendButtons, formatUserAnswer, getButtonFromText, mergeOverlappingText } from './utils';

import type {
	TutorConversationData,
	TutorHistoryMessage,
	TutorMessage,
	TutorMessagePayload,
} from '@/features/ai-tutor/types';
import type { Action } from '@/shared/types';

export const EMPTY_CHAT_MESSAGE_OBJ: TutorMessagePayload = {
	tutor_question: '',
	user_answer: '',
};

export interface AiTutorChatState {
	messages: TutorMessage[];
	showChat: boolean;
	historyLoading: boolean;
	sending: boolean;
	lastEvaluatedKey: string | null;
	messageCounter: number;
	messageObj: TutorMessagePayload;
	conversationData: Partial<TutorConversationData>;
}

const initialState: AiTutorChatState = {
	messages: [],
	showChat: false,
	historyLoading: false,
	sending: false,
	lastEvaluatedKey: null,
	messageCounter: 0,
	messageObj: { ...EMPTY_CHAT_MESSAGE_OBJ },
	conversationData: {},
};

function buildHistoryMessages(history: TutorHistoryMessage[], messageCounter: number) {
	const messages: TutorMessage[] = [];
	let nextCounter = messageCounter;

	for (const message of history) {
		nextCounter += 1;
		const messageId = nextCounter;
		const buttonFromText = getButtonFromText(message.chat_log.assistant);
		let botMessage: TutorMessage = {
			id: messageId,
			sender: 'Bot',
			text: 'show_loader',
			timestamp: message.added_on,
			complete: false,
			loading: false,
			loaderRan: false,
			sessionId: message.session_id,
		};

		if (buttonFromText) {
			botMessage = appendButtons(botMessage, buttonFromText.buttons);
		} else {
			botMessage.text = DOMPurify.sanitize(message.chat_log.assistant);
		}

		messages.push(botMessage);

		if (message.chat_log.user !== '') {
			messages.push({
				sender: 'User',
				text: formatUserAnswer(message.chat_log.user),
				timestamp: message.added_on,
				complete: true,
				loading: false,
				sessionId: message.session_id,
			});
		}
	}

	return {
		messages,
		messageCounter: nextCounter,
	};
}

function reduceAddBotMessage(state: AiTutorChatState, payload: any): AiTutorChatState {
	const { id, text, complete, showLoader, inputText, sessionId, timestamp } = payload;
	const timeToShow = timestamp === null ? new Date().toISOString() : timestamp;
	const existingMessageIndex = state.messages.findIndex((msg) => msg.id === id);

	if (showLoader) {
		return {
			...state,
			messages: [
				...state.messages,
				{
					id,
					sender: 'Bot',
					text: 'show_loader',
					timestamp: timeToShow,
					complete: false,
					loading: true,
					loaderRan: true,
					sessionId,
				},
			],
		};
	}

	if (existingMessageIndex < 0) {
		return state;
	}

	const messages = [...state.messages];
	const existingMessage = messages[existingMessageIndex];
	const buttonFromText = getButtonFromText(text);
	let updatedMessage = { ...existingMessage };
	let messageObj = state.messageObj;

	if (existingMessage.loaderRan === false) {
		if (buttonFromText) {
			updatedMessage = appendButtons(updatedMessage, buttonFromText.buttons);
		} else {
			const sanitizedText = DOMPurify.sanitize(text);
			updatedMessage.text = mergeOverlappingText(updatedMessage.text, sanitizedText);
		}
		if (complete) {
			updatedMessage.complete = true;
			messageObj = {
				tutor_question: updatedMessage.text,
				user_answer:
					typeof inputText === 'object' && inputText !== null
						? inputText.user_answer
						: '',
			};
		}
		updatedMessage.sessionId = sessionId;
		updatedMessage.loading = !complete;
		updatedMessage.loaderRan = false;
	} else {
		if (buttonFromText) {
			updatedMessage = appendButtons(updatedMessage, buttonFromText.buttons);
		} else {
			updatedMessage.text = DOMPurify.sanitize(text);
		}
		updatedMessage.complete = false;
		updatedMessage.loading = !complete;
		updatedMessage.loaderRan = false;
		updatedMessage.sessionId = sessionId;
	}

	messages[existingMessageIndex] = updatedMessage;

	return {
		...state,
		messages,
		messageObj,
	};
}

export default function aiTutorChatReducer(state = initialState, action: Action): AiTutorChatState {
	const { type, payload } = action;

	switch (type) {
		case chatActionTypes.CLOSE_CHAT_INTERFACE:
			return {
				...state,
				showChat: payload.showChat,
			};

		case chatActionTypes.CLEAR_CHAT_SESSION:
			return {
				...state,
				messages: [],
				showChat: false,
				historyLoading: false,
				sending: false,
				lastEvaluatedKey: null,
				messageCounter: 0,
				messageObj: { ...EMPTY_CHAT_MESSAGE_OBJ },
				conversationData: {},
			};

		case chatActionTypes.REFRESH_CHAT_INTERFACE:
			return {
				...state,
				messages: [],
				showChat: true,
			};

		case chatActionTypes.ADD_USER_MESSAGE: {
			const { text, sessionId, timestamp } = payload;
			const timeToShow = timestamp === null ? new Date().toISOString() : timestamp;

			return {
				...state,
				messages: [
					...state.messages,
					{
						sender: 'User',
						text,
						timestamp: timeToShow,
						complete: true,
						loading: false,
						sessionId,
					},
				],
			};
		}

		case chatActionTypes.ADD_BOT_MESSAGE:
			return reduceAddBotMessage(state, payload);

		case chatActionTypes.PREPEND_CHAT_HISTORY_MESSAGES: {
			const { messages, messageCounter } = buildHistoryMessages(
				payload.history,
				state.messageCounter,
			);

			return {
				...state,
				messages: [...messages, ...state.messages],
				messageCounter,
			};
		}

		case chatActionTypes.REMOVE_BUTTONS_FROM_PREVIOUS_MESSAGE: {
			const lastBotMsgIndex = [...state.messages]
				.reverse()
				.findIndex((msg) => msg.sender !== 'User' && msg.button);

			if (lastBotMsgIndex === -1) return state;

			const idx = state.messages.length - 1 - lastBotMsgIndex;
			const messages = [...state.messages];
			messages[idx] = {
				...messages[idx],
				button: undefined,
			};

			return {
				...state,
				messages,
			};
		}

		case chatActionTypes.SET_CHAT_CONVERSATION_DATA:
			return {
				...state,
				conversationData: payload.conversationData,
			};

		case chatActionTypes.SET_CHAT_HISTORY_LOADING:
			return {
				...state,
				historyLoading: payload.historyLoading,
			};

		case chatActionTypes.SET_CHAT_SENDING:
			return {
				...state,
				sending: payload.sending,
			};

		case chatActionTypes.SET_CHAT_LAST_EVALUATED_KEY:
			return {
				...state,
				lastEvaluatedKey: payload.lastEvaluatedKey,
			};

		case chatActionTypes.SET_CHAT_MESSAGE_COUNTER:
			return {
				...state,
				messageCounter: payload.messageCounter,
			};

		case chatActionTypes.SET_CHAT_MESSAGE_OBJ:
			return {
				...state,
				messageObj: payload.messageObj,
			};

		case chatActionTypes.RESET_CHAT_MESSAGE_OBJ:
			return {
				...state,
				messageObj: { ...EMPTY_CHAT_MESSAGE_OBJ },
			};

		default:
			return state;
	}
}
